import './PageSidePanel.scss';

import GenericFormControl from '@components/Common/GenericFormControl';
import { DenominatorLabels, EXCLUDED_DECKLINES_FOR_DENOMINATORS } from '@constants/page';
import { useDeckContext } from '@context/DeckContext';
import { useDecklinesContext } from '@context/DecklinesContext';
import { useKPILibraryContext } from '@context/KPILibraryContext';
import { usePageContext } from '@context/PageContext';
import { useTOCContext } from '@context/TOCContext';
import ConfirmDialog from '@deckManagement/components/Common/Dialogs/ConfirmDialog';
import { KPIDriversDisplay } from '@deckManagement/components/Common/KPIDriversDisplay';
import TableOfContentsSelector from '@deckManagement/components/Common/SidePanel/TableOfContentsSelector';
import KPILibrary from '@deckManagement/components/KPILibrary/KPILibrary';
import CalculatedKPIPanel from '@deckManagement/components/Page/SidePanel/CalculatedKPIPanel';
import useTocSelectorConfig from '@deckManagement/hooks/useTocSelectorConfig';
import useVariantDriverSync from '@deckManagement/hooks/useVariantDriverSync';
import { KPILibraryHandle, KPILibraryView } from '@deckManagement/types/KPILibrary.types';
import { ViewType } from '@deckManagement/types/TableOfContentsSelector.types';
import { Alert, Chip, Divider, Stack, Tab, Tabs } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { CalculatedKPIValues, KPIDrivers } from '@services/api/deckManagement/graphql/schema/API';
import { useGetVariantsForRootKPI } from '@services/api/deckManagement/hooks/useKPIVariants';
import {
  EMPTY_KPI_DRIVERS,
  generateBaseKpiId,
  generateDriverDisplayOptions,
  hasDriverMismatch,
} from '@utils/kpiDriverHelpers.ts';
import { isEmpty, isNil, isNull } from 'lodash';
import { SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { LinkedKPIChangeItem } from 'types/DecklineNameInput.types';
import { EnhancedKPIVariant } from 'types/KPILibraryContext.types';
import { DriverDisplayOptionMap } from 'types/PageContext.types';
import { DriverType, KpiDimension, PageSidePanelDropDown } from 'types/PageSidePanel.types';

import DecklineFormattingOptions from './DecklineFormattingOptions';
import DecklineNameInput from './DecklineNameInput';
import useSubtotalHelper from './SubtotalHelper';
import VariantOverridesDisplay from './VariantOverridesDisplay';

/**
 * Side panel component for a Page
 * Displays contextual options with User actions, for the Page view
 */
const PageSidePanel = () => {
  const { deckId } = useParams() as { deckId: string };
  const { rootNodes } = useDeckContext();
  const { getTocItemsByGroup } = useTOCContext();
  const {
    selectedDeckline,
    updatePageDeckline,
    updateDecklineDriverActiveState,
    updateDecklineDenominatorDriverActiveState,
    selectedDecklineDriverOptions,
    decklineType,
    setDecklineType,
    selectedDenominatorDriverOptions,
    denominatorType,
    setDenominatorType,
    subtotalMap,
    setIsCreatingSubtotal,
    updateDecklinesForPageDriverChanges,
    isLoading: isDecklinesLoading,
  } = useDecklinesContext();
  const {
    isLoading,
    currentPageDriverOptions,
    modifiedPageProperties,
    updatePageDriverActiveState,
    pageRowData,
    pageLockStatus,
  } = usePageContext();

  const { page_drivers: currentPageKPIDrivers, denominator_label: denominatorLabel } = modifiedPageProperties || {};

  // Force re-render when pageRowData changes
  useEffect(() => {}, [pageRowData]);

  const {
    calculateSubtotal,
    handleStartRowChange,
    handleEndRowChange,
    startInputRef,
    endInputRef,
    highlightedRange,
    showUnsavedDialog,
    setShowUnsavedDialog,
    checkUnsavedChanges,
    discardUnsavedChanges,
    applySubtotalRangeHighlighting,
  } = useSubtotalHelper(pageRowData);

  const [activeTab, setActiveTab] = useState<DriverType>(DriverType.PAGE);
  const [localKpiName, setLocalKpiName] = useState<string>('');
  const [, setLocalSelectedDecklineId] = useState<string | null>(null);
  // Lifted from VariantOverridesDisplay so collapse state persists across deckline selections
  const [showInheritedDimensions, setShowInheritedDimensions] = useState(false);

  const kpiLibraryRef = useRef<KPILibraryHandle>(null);
  const {
    pendingCreations: { allRootKPIs, getEnhancedVariants },
  } = useKPILibraryContext();

  // Fetch variants for the selected deckline's root KPI (hits React Query cache)
  const { data: fetchedVariants = [], isFetching: isVariantsFetching } = useGetVariantsForRootKPI({
    root_kpi_id: selectedDeckline?.root_kpi_id ?? '',
    options: { enabled: !!selectedDeckline?.root_kpi_id },
  });

  /** The selected deckline's variant, enhanced with isNew status */
  const selectedEnhancedVariant = useMemo(() => {
    if (!selectedDeckline?.root_kpi_id || !selectedDeckline?.kpi_variant_id) return null;
    const enhanced = getEnhancedVariants(selectedDeckline.root_kpi_id, fetchedVariants);
    return enhanced.find((v) => v.kpi_variant_id === selectedDeckline.kpi_variant_id) ?? null;
  }, [selectedDeckline?.root_kpi_id, selectedDeckline?.kpi_variant_id, getEnhancedVariants, fetchedVariants]);

  const syncVariantDrivers = useVariantDriverSync();

  // When data is loading, ensures the "Page Drivers" tab is selected.
  useEffect(() => {
    if (isLoading || isDecklinesLoading) {
      setActiveTab(DriverType.PAGE);
    }
  }, [isDecklinesLoading, isLoading]);

  // Calculate start and end row values for display
  const getSubtotalRowValues = useCallback(() => {
    let startRow = selectedDeckline?.subtotal_start_index || '';
    let endRow = selectedDeckline?.subtotal_end_index || '';

    // If values are empty but this is a subtotal row, calculate from subtotalMap
    if (selectedDeckline && !isNil(selectedDeckline.subtotal_formula) && (!startRow || !endRow)) {
      const subtotalKey = selectedDeckline.deckline_id;
      const childRows = subtotalMap?.get(subtotalKey!);
      if (childRows && Array.isArray(childRows) && childRows.length > 0) {
        const rowNumbers = childRows.map((row) => Number(row.rowNumber)).filter((n) => !Number.isNaN(n));
        if (rowNumbers.length > 0) {
          startRow = Math.min(...rowNumbers).toString();
          endRow = Math.max(...rowNumbers).toString();
        }
      }
    }

    return { startRow, endRow };
  }, [selectedDeckline, subtotalMap]);

  // Apply highlighting when a subtotal row is clicked
  useEffect(() => {
    // Skip if user is actively editing the range (highlightedRange exists)
    if (highlightedRange) return;

    if (selectedDeckline && !isNil(selectedDeckline.subtotal_formula) && pageRowData) {
      const { startRow, endRow } = getSubtotalRowValues();
      if (startRow && endRow) {
        applySubtotalRangeHighlighting(Number(startRow), Number(endRow), pageRowData);
      }
    }
  }, [selectedDeckline, applySubtotalRangeHighlighting, getSubtotalRowValues, pageRowData, highlightedRange]);

  const { startRow, endRow } = getSubtotalRowValues();

  // Sync local state when selectedDeckline changes (only when switching decklines)
  useEffect(() => {
    const currentKpiName = selectedDeckline?.kpi_name ?? '';
    setLocalKpiName(currentKpiName);

    // NOTE: ESLint expects `selectedDeckline` as the dependency, but only want this to run when internalId changes.
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [selectedDeckline?.metadata?.internalId]);

  /**
   * When the deckline type changes, we need to ensure the denominator tab is not selected
   * if the deckline type is not compatible with denominators.
   */
  useEffect(() => {
    const excludeDenominator = EXCLUDED_DECKLINES_FOR_DENOMINATORS.includes(decklineType);

    setActiveTab((prev) => {
      // If denominator tab is selected but no longer supported, fall back to deckline tab
      if (prev === DriverType.DENOMINATOR_FOR_PERCENT && excludeDenominator) {
        return DriverType.DECKLINE;
      }

      return prev;
    });
  }, [decklineType]);

  /**
   * Dropdown options for Deckline types (Blank Line, Base KPI, Calculated KPI, etc.)
   */
  const pageSidePanelDropDown = useMemo(
    () => [
      {
        value: PageSidePanelDropDown.BLANK_LINE as PageSidePanelDropDown,
        label: PageSidePanelDropDown.BLANK_LINE,
        id: 'blank-line',
      },
      {
        value: PageSidePanelDropDown.ADJUSTMENT as PageSidePanelDropDown,
        label: PageSidePanelDropDown.ADJUSTMENT,
        id: 'adjustment',
      },
      {
        value: PageSidePanelDropDown.CALCULATED_ADJUSTMENT as PageSidePanelDropDown,
        label: PageSidePanelDropDown.CALCULATED_ADJUSTMENT,
        id: 'calculated-adjustment',
      },
      {
        value: PageSidePanelDropDown.BASE_KPI as PageSidePanelDropDown,
        label: PageSidePanelDropDown.BASE_KPI,
        id: 'base-kpi',
      },
      {
        value: PageSidePanelDropDown.CALCULATED_KPI as PageSidePanelDropDown,
        label: PageSidePanelDropDown.CALCULATED_KPI,
        id: 'calculated-kpi',
      },
      {
        value: PageSidePanelDropDown.SUBTOTAL as PageSidePanelDropDown,
        label: PageSidePanelDropDown.SUBTOTAL,
        id: 'subtotal',
        disabled: decklineType !== PageSidePanelDropDown.BLANK_LINE && decklineType !== PageSidePanelDropDown.SUBTOTAL,
      },
    ],
    [decklineType],
  );

  const denominatorTypeDropDown = useMemo(
    () => [
      {
        value: PageSidePanelDropDown.NONE,
        label: 'Not Applicable',
        id: 'not-applicable',
      },
      {
        value: PageSidePanelDropDown.BASE_KPI as PageSidePanelDropDown,
        label: PageSidePanelDropDown.BASE_KPI,
        id: 'base-kpi',
      },
      {
        value: PageSidePanelDropDown.CALCULATED_KPI as PageSidePanelDropDown,
        label: PageSidePanelDropDown.CALCULATED_KPI,
        id: 'calculated-kpi',
      },
    ],
    [],
  );

  /**
   * Changes the active tab when selectedDeckline changes.
   */
  useEffect(() => {
    // Set the correct tab based on when selectedDeckline changes
    // We track internalId changes because the selectedDeckline may update from page driver changes.
    setLocalSelectedDecklineId((prev) => {
      if (selectedDeckline?.metadata.internalId !== prev) {
        setActiveTab((prevTab) => {
          if (isNil(selectedDeckline)) {
            return DriverType.PAGE;
          }

          // If we have a selectedDeckline and we're NOT already on a deckline-specific tab, switch to Deckline.
          if (![DriverType.DECKLINE, DriverType.DENOMINATOR_FOR_PERCENT].includes(prevTab)) {
            return DriverType.DECKLINE;
          }

          return prevTab;
        });
        return selectedDeckline?.metadata.internalId ?? null;
      }
      return prev;
    });
  }, [selectedDeckline]);

  /**
   * Handles the change in driver selection between Page and Deckline
   * @param _event - The event that triggered the change
   * @param value - The new value of the driver selection
   */
  const handleDriverTabChange = useCallback(
    (_event: SyntheticEvent, value: string) => {
      const newTab = value as DriverType;
      if (newTab !== null) {
        if (checkUnsavedChanges()) {
          return; // Don't change tab if there are unsaved changes
        }
        setActiveTab(newTab);
      }
    },
    [checkUnsavedChanges],
  );

  /**
   * Handles KPI Selection , Subtotal , Adjustment , Blank Line
   * @param event - The select event which includes the PageSidePanelDropDown as a value
   */
  const handleKPISelection = useCallback(
    (event: SelectChangeEvent<PageSidePanelDropDown>) => {
      const newType = event.target.value as PageSidePanelDropDown;
      setDecklineType(newType);
      setIsCreatingSubtotal(newType === PageSidePanelDropDown.SUBTOTAL);
    },
    [setDecklineType, setIsCreatingSubtotal],
  );

  /**
   * Handles KPI Selection , Subtotal , Adjustment , Blank Line
   * @param event - The select event which includes the PageSidePanelDropDown as a value
   */
  const handleDenominatorTypeSelection = useCallback(
    (event: SelectChangeEvent<PageSidePanelDropDown>) => {
      const newType = event.target.value as PageSidePanelDropDown;
      setDenominatorType(newType);
    },
    [setDenominatorType],
  );

  /**
   * Handles updates to the Deckline Driver segments
   * @param key - The index of the segment in the segmentData array
   * @param segmentData - The segment data object
   * @param updates - The updates to be applied to the segment data
   * @param newKpiId - The new KPI ID
   */
  const handleDecklineDriverUpdate = useCallback(
    (_key: KpiDimension, updates: Partial<KPIDrivers>, updatedDriverMap: DriverDisplayOptionMap) => {
      if (!selectedDeckline) return;
      syncVariantDrivers(selectedDeckline, updates, updatedDriverMap, selectedEnhancedVariant);
    },
    [selectedDeckline, syncVariantDrivers, selectedEnhancedVariant],
  );

  /**
   * Handles updates to the Denominator Driver segments
   * @param key - The index of the segment in the segmentData array
   * @param segmentData - The segment data object
   * @param updates - The updates to be applied to the segment data
   * @param newKpiId - The new KPI ID
   */
  const handleDenominatorDriverUpdate = useCallback(
    (_key: KpiDimension, updates: Partial<KPIDrivers>, updatedDriverMap: DriverDisplayOptionMap) => {
      if (!selectedDeckline) {
        return;
      }

      const newData = {
        ...selectedDeckline.denominator_kpi_id_drivers,
        ...updates,
      } as KPIDrivers;

      updatePageDeckline(selectedDeckline.metadata.internalId, 'denominator_kpi_id_drivers', newData);
      updatePageDeckline(
        selectedDeckline.metadata.internalId,
        'denominator_kpi_id_for_pct',
        generateBaseKpiId(updatedDriverMap, rootNodes),
      );
    },
    [selectedDeckline, updatePageDeckline, rootNodes],
  );

  /**
   * Handles updates to the Calculated KPI segment
   * @param newId - The new KPI ID
   * @param newFormula - The new KPI formula
   */
  const handleCalculatedKpiChange = useCallback(
    (newId: string, newFormula: string) => {
      if (!selectedDeckline) {
        return;
      }

      updatePageDeckline(selectedDeckline.metadata.internalId, 'kpi_id', newId);
      updatePageDeckline(selectedDeckline.metadata.internalId, 'kpi_formula', newFormula);
      // Ensure `is_calculated_kpi` is 'Y' when we update.
      updatePageDeckline(selectedDeckline.metadata.internalId, 'is_calculated_kpi', CalculatedKPIValues.Y);
    },
    [selectedDeckline, updatePageDeckline],
  );

  /**
   * Handles updates to the Calculated KPI segment
   * @param newId - The new KPI ID
   * @param newFormula - The new KPI formula
   */
  const handleDenominatorCalculatedKpiChange = useCallback(
    (newId: string, newFormula: string) => {
      if (!selectedDeckline) {
        return;
      }

      updatePageDeckline(selectedDeckline.metadata.internalId, 'denominator_kpi_id_for_pct', newId);
      updatePageDeckline(selectedDeckline.metadata.internalId, 'denominator_kpi_formula', newFormula);
      // Ensure `denominator_is_calculated_kpi` is 'Y' when we update.
      updatePageDeckline(selectedDeckline.metadata.internalId, 'denominator_is_calculated_kpi', CalculatedKPIValues.Y);
    },
    [selectedDeckline, updatePageDeckline],
  );

  /**
   * Handles toggling the active state of a segment
   * @param index - The index of the segment in the segmentData array
   */
  const handleSwitchToggle = useCallback(
    (key: KpiDimension, checked: boolean) => {
      if (activeTab === DriverType.PAGE) {
        updatePageDriverActiveState(key, checked, updateDecklinesForPageDriverChanges);
      } else if (activeTab === DriverType.DECKLINE) {
        updateDecklineDriverActiveState(key, checked);
      } else {
        // Deckline Denominator
        updateDecklineDenominatorDriverActiveState(key, checked);
      }
    },
    [
      activeTab,
      updatePageDriverActiveState,
      updateDecklinesForPageDriverChanges,
      updateDecklineDriverActiveState,
      updateDecklineDenominatorDriverActiveState,
    ],
  );

  // To determine if the Page data is still loading or not
  const dataLoaded = !isLoading && !isEmpty(currentPageDriverOptions);

  /**
   * Renders the readonly Page Settings display with dimension label/value pairs
   */
  const renderPageDriverDisplay = useCallback(() => {
    if (!isNull(currentPageDriverOptions)) {
      return (
        <Stack className='page-settings-readonly'>
          <Divider variant='middle' flexItem>
            <Chip label='Page Settings' size='small' variant='outlined' />
          </Divider>
          <KPIDriversDisplay driverOptions={currentPageDriverOptions} renderMode='readonly' />
          {denominatorLabel && (
            <>
              <Divider variant='middle' flexItem>
                <Chip label={DenominatorLabels[denominatorLabel]} size='small' variant='outlined' />
              </Divider>
              {/**
               * TODO: For now, we're reusing the same `currentPageDriverOptions` for the denominator
               *   since the denominator since `denominator_drivers` doesn't exist on the Page object.
               *   This will need to be adjusted when we support different drivers for the denominator.
               *   (See Schema: https://tiny.amazon.com/3xhnemqx) */}
              <KPIDriversDisplay driverOptions={currentPageDriverOptions} renderMode='readonly' />
            </>
          )}
        </Stack>
      );
    }

    return null;
  }, [currentPageDriverOptions, denominatorLabel]);

  /**
   * Renders the segment display list for Base KPI
   */
  const renderDenominatorDriverDisplay = useCallback(() => {
    if (!selectedDeckline) {
      return <Typography className='page-side-panel-text-only'>Select a deckline to continue</Typography>;
    }
    if (!isNull(selectedDenominatorDriverOptions)) {
      return (
        <KPIDriversDisplay
          driverOptions={selectedDenominatorDriverOptions}
          renderMode='dimensionPicker'
          onToggleSwitch={handleSwitchToggle}
          onChange={handleDenominatorDriverUpdate}
          disabled={pageLockStatus.isEditingDisabled}
        />
      );
    }

    return null;
  }, [
    handleDenominatorDriverUpdate,
    handleSwitchToggle,
    selectedDeckline,
    selectedDenominatorDriverOptions,
    pageLockStatus.isEditingDisabled,
  ]);

  const renderDenominatorCalculatedKpiDisplay = useCallback(() => {
    if (!selectedDeckline) {
      return <Typography className='page-side-panel-text-only'>Select a deckline to continue</Typography>;
    }

    return (
      <CalculatedKPIPanel
        initialFormula={selectedDeckline.denominator_kpi_formula!}
        initialKpiId={selectedDeckline.denominator_kpi_id_for_pct}
        pageDrivers={currentPageDriverOptions!}
        onFormulaChange={handleDenominatorCalculatedKpiChange}
        alwaysInclude={[KpiDimension.ACCOUNT]}
        disabled={pageLockStatus.isEditingDisabled}
      />
    );
  }, [
    currentPageDriverOptions,
    handleDenominatorCalculatedKpiChange,
    pageLockStatus.isEditingDisabled,
    selectedDeckline,
  ]);

  /**
   * Renders the Calculated KPI panel for the selected Deckline
   */
  const renderCalculatedKpiSegmentDisplay = useCallback(() => {
    if (!selectedDeckline) {
      return <Typography className='page-side-panel-text-only'>Select a deckline to continue</Typography>;
    }

    return (
      <CalculatedKPIPanel
        initialFormula={selectedDeckline.kpi_formula!}
        initialKpiId={selectedDeckline.kpi_id}
        pageDrivers={currentPageDriverOptions!}
        onFormulaChange={handleCalculatedKpiChange}
        disabled={pageLockStatus.isEditingDisabled}
      />
    );
  }, [currentPageDriverOptions, handleCalculatedKpiChange, pageLockStatus.isEditingDisabled, selectedDeckline]);

  /**
   * Renders the Deckline Adjustment Panel Display
   */
  const renderDecklineAdjustmentDisplay = useCallback(() => {
    if (!selectedDeckline) {
      return <Typography className='page-side-panel-text-only'>Select a deckline to continue</Typography>;
    }

    return (
      <List className='page-side-panel-calculated-kpi segments'>
        <ListItem disablePadding>
          <ListItemText
            primary='Adjusting Definition'
            secondary={selectedDeckline.kpi_id}
            slotProps={{ primary: { variant: 'body2' }, secondary: { variant: 'body1' } }}
          />
        </ListItem>
      </List>
    );

    // return <TextField fullWidth label='Add Adjusting definition' margin='normal' className='text-field-background' />;
  }, [selectedDeckline]);

  /**
   * Renders the Deckline Subtotal Panel Display
   */
  const renderDecklineSubtotalDisplay = useCallback(() => {
    return (
      <Box>
        <Box className='subtotal'>
          <TextField
            key={`start-${selectedDeckline?.metadata?.internalId}-${startRow}`}
            label='Start Row'
            margin='normal'
            className='subtotal-text-field'
            defaultValue={startRow}
            onChange={handleStartRowChange}
            inputRef={startInputRef}
            disabled={pageLockStatus.isEditingDisabled}
          />
          <Box className='separator-container'> - </Box>
          <TextField
            key={`end-${selectedDeckline?.metadata?.internalId}-${endRow}`}
            label='End Row'
            margin='normal'
            className='subtotal-text-field'
            defaultValue={endRow}
            onChange={handleEndRowChange}
            inputRef={endInputRef}
            disabled={pageLockStatus.isEditingDisabled}
          />
        </Box>
        <Box className='subtotal-button-container'>
          <Button
            variant='contained'
            onClick={() => {
              calculateSubtotal();
              setIsCreatingSubtotal(false);
            }}
            color='primary'
          >
            Save Subtotal
          </Button>
        </Box>
      </Box>
    );
  }, [
    selectedDeckline?.metadata?.internalId,
    startRow,
    handleStartRowChange,
    startInputRef,
    endRow,
    handleEndRowChange,
    endInputRef,
    calculateSubtotal,
    setIsCreatingSubtotal,
    pageLockStatus.isEditingDisabled,
  ]);

  /**
   * Handles updated the Deckline Name (KPI Name).
   */
  const handleDecklineNameChange = useCallback(
    (name: string) => {
      const newValue = name.trimStart();
      setLocalKpiName((prev) => {
        if (selectedDeckline && newValue !== prev) {
          setTimeout(() => {
            updatePageDeckline(selectedDeckline.metadata.internalId, 'kpi_name', newValue);
          }, 20);
          return newValue;
        }
        return prev;
      });
    },
    [selectedDeckline, updatePageDeckline],
  );

  /**
   * Handles Linked KPI changes (Root KPI and Variant selections).
   * Updates root_kpi_id, kpi_variant_id, and deckline_drivers on the selected deckline.
   *
   * Three mutually exclusive driver-initialization paths:
   * - Existing variant: merges variant_overrides onto page_drivers
   * - New (pending) variant: starts with empty drivers for user to fill
   * - Variant cleared (null): resets drivers to page defaults
   */
  const handleLinkedKpiChange = useCallback(
    (rootKpi: LinkedKPIChangeItem, variant: EnhancedKPIVariant | null) => {
      if (!selectedDeckline) return;
      const { internalId } = selectedDeckline.metadata;

      if (rootKpi.id !== undefined) {
        updatePageDeckline(internalId, 'root_kpi_id', rootKpi.id);
      }
      if (rootKpi.name !== undefined) {
        updatePageDeckline(internalId, 'kpi_name', rootKpi.name ?? '');
        setLocalKpiName(rootKpi.name ?? '');
      }

      updatePageDeckline(internalId, 'kpi_variant_id', variant?.kpi_variant_id ?? null);

      // For existing variants, apply overrides merged with page_drivers to deckline_drivers
      if (variant && !variant.isNew) {
        if (currentPageKPIDrivers) {
          const mergedDrivers = {
            ...currentPageKPIDrivers,
            ...(variant.variant_overrides ?? {}),
          } as KPIDrivers;
          updatePageDeckline(internalId, 'deckline_drivers', mergedDrivers);
          const driverMap = generateDriverDisplayOptions(mergedDrivers, currentPageKPIDrivers);
          updatePageDeckline(internalId, 'kpi_id', generateBaseKpiId(driverMap, rootNodes));
        } else {
          // Page drivers not yet available — apply variant_overrides only
          updatePageDeckline(internalId, 'deckline_drivers', (variant.variant_overrides ?? null) as KPIDrivers | null);
          updatePageDeckline(internalId, 'kpi_id', '');
        }
      } else if (variant?.isNew) {
        // New variants start with empty drivers for user to fill
        updatePageDeckline(internalId, 'deckline_drivers', EMPTY_KPI_DRIVERS);
        updatePageDeckline(internalId, 'kpi_id', '');
      } else {
        // Variant cleared — reset drivers to page defaults (no exception)
        updatePageDeckline(internalId, 'deckline_drivers', null);
        updatePageDeckline(internalId, 'kpi_id', '');
      }
    },
    [selectedDeckline, updatePageDeckline, currentPageKPIDrivers, rootNodes],
  );

  /**
   * Opens the KPI Library dialog. If a root_kpi_id is selected, opens in Variant View;
   * otherwise opens in Root KPI View. Applies the selection to the deckline on assign.
   */
  const openKPILibrary = useCallback(async () => {
    if (!selectedDeckline) return;
    const rootKpiId = selectedDeckline.root_kpi_id;
    const rootKPI = rootKpiId ? (allRootKPIs.find((kpi) => kpi.root_kpi_id === rootKpiId) ?? null) : null;
    const hasVariant = !!selectedDeckline.kpi_variant_id;
    let view: KPILibraryView | undefined;
    if (hasVariant) {
      view = KPILibraryView.EDIT_VARIANT_VIEW;
    } else if (rootKPI) {
      view = KPILibraryView.VARIANT_VIEW;
    }

    const selection = await kpiLibraryRef.current?.open(view);
    if (!selection) return;

    handleLinkedKpiChange(
      { id: selection.rootKPI.root_kpi_id, name: selection.rootKPI.root_kpi_name },
      { ...selection.variant, isNew: false },
    );
  }, [selectedDeckline, allRootKPIs, handleLinkedKpiChange]);

  /**
   * Renders the segment display list for Decklines using Base KPI mode.
   * - New (pending) variants: editable KPIDriversDisplay with dimension pickers
   * - Existing variants: read-only VariantOverridesDisplay with overrides/inherited sections
   */
  const renderDecklineDriverDisplay = useCallback(() => {
    if (!selectedDeckline) {
      return <Typography className='page-side-panel-text-only'>Select a deckline to continue</Typography>;
    }

    // No variant selected yet — don't show driver display
    if (!selectedDeckline.kpi_variant_id) {
      return null;
    }

    // When selectedEnhancedVariant is null (still resolving), treat as existing — show read-only
    // until we confirm it's a new pending variant. Once resolved, isNew=true switches to pickers.
    if (!selectedEnhancedVariant?.isNew && selectedDecklineDriverOptions) {
      return (
        <VariantOverridesDisplay
          driverOptions={selectedDecklineDriverOptions}
          // Falls back to deckline_drivers to infer overrides while variant data is loading
          variantOverrides={selectedEnhancedVariant?.variant_overrides ?? selectedDeckline.deckline_drivers ?? null}
          showInherited={showInheritedDimensions}
          onToggleInherited={() => setShowInheritedDimensions((prev) => !prev)}
          // Opens KPI Library dialog to the variant edit view
          onFieldClick={openKPILibrary}
          disabled={isVariantsFetching || pageLockStatus.isEditingDisabled}
        />
      );
    }

    // New variant: editable dimension pickers
    if (!isNull(selectedDecklineDriverOptions)) {
      return (
        <KPIDriversDisplay
          className='deckline-drivers-display'
          driverOptions={selectedDecklineDriverOptions}
          renderMode='dimensionPicker'
          onToggleSwitch={handleSwitchToggle}
          onChange={handleDecklineDriverUpdate}
          disabled={pageLockStatus.isEditingDisabled}
        />
      );
    }

    return null;
  }, [
    selectedDeckline,
    isVariantsFetching,
    selectedEnhancedVariant,
    selectedDecklineDriverOptions,
    showInheritedDimensions,
    handleSwitchToggle,
    handleDecklineDriverUpdate,
    pageLockStatus.isEditingDisabled,
    openKPILibrary,
  ]);

  /**
   * Handles rendering the Deckline Toolbar (Deckline Name, Deckline Type, Formatting Controls)
   */
  const renderDecklineEditToolbar = useCallback(() => {
    if (!selectedDeckline) {
      return null;
    }
    return (
      <Box className='deckline-specific-section'>
        <FormControl size='small' fullWidth margin='dense'>
          <InputLabel id='deckline-type-label'>Type</InputLabel>
          <Select
            labelId='deckline-type-label'
            value={decklineType}
            onChange={handleKPISelection}
            label='Type'
            disabled={pageLockStatus.isEditingDisabled}
            size='small'
          >
            {pageSidePanelDropDown.map((option) => (
              <MenuItem key={option.id} value={option.value} disabled={option.disabled}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <DecklineNameInput
          value={localKpiName}
          rootKpiId={selectedDeckline?.root_kpi_id}
          kpiVariantId={selectedDeckline?.kpi_variant_id}
          decklineType={decklineType}
          onNameChange={handleDecklineNameChange}
          onLinkedKpiChange={handleLinkedKpiChange}
          onOpenKPILibrary={openKPILibrary}
          disabled={pageLockStatus.isEditingDisabled}
        />
        <Box className='deckline-formatting-controls'>
          <DecklineFormattingOptions isEditingDisabled={pageLockStatus.isEditingDisabled} />
        </Box>
      </Box>
    );
  }, [
    decklineType,
    handleDecklineNameChange,
    handleLinkedKpiChange,
    handleKPISelection,
    localKpiName,
    openKPILibrary,
    pageSidePanelDropDown,
    selectedDeckline,
    pageLockStatus.isEditingDisabled,
  ]);

  /**
   * Handles rendering the Denominator Edit Toolbar
   */
  const renderDenominatorEditToolbar = useCallback(() => {
    return (
      <Box className='deckline-specific-section denominator-section'>
        <Typography gutterBottom component='div'>
          <GenericFormControl
            value={denominatorType ?? PageSidePanelDropDown.NONE}
            onChange={handleDenominatorTypeSelection}
            options={denominatorTypeDropDown}
            disabled={pageLockStatus.isEditingDisabled}
            className='kpi-selector-dropdown'
          />
        </Typography>
      </Box>
    );
  }, [denominatorType, denominatorTypeDropDown, handleDenominatorTypeSelection, pageLockStatus.isEditingDisabled]);

  /**
   * Renders the content based on the active driver tab and KpiType for Deckline
   */
  const renderContent = useCallback(() => {
    if (dataLoaded) {
      // Page Driver Content
      if (activeTab === DriverType.PAGE) {
        return renderPageDriverDisplay();
      }

      // Deckline Driver Content
      if (activeTab === DriverType.DECKLINE) {
        if (selectedDeckline) {
          return (
            <Box>
              {hasDriverMismatch(selectedDeckline?.deckline_drivers, currentPageKPIDrivers) && (
                <Alert severity='warning' icon={false} className='exception-line'>
                  Exception
                </Alert>
              )}
              {/* Deckline Specific Toolbar */}
              {renderDecklineEditToolbar()}
              <Box className='page-side-panel-content-body'>
                {/* Deckline KpiType-specific Content */}
                {decklineType === PageSidePanelDropDown.BASE_KPI && renderDecklineDriverDisplay()}
                {decklineType === PageSidePanelDropDown.CALCULATED_KPI && renderCalculatedKpiSegmentDisplay()}
                {(decklineType === PageSidePanelDropDown.ADJUSTMENT ||
                  decklineType === PageSidePanelDropDown.CALCULATED_ADJUSTMENT) &&
                  renderDecklineAdjustmentDisplay()}
                {decklineType === PageSidePanelDropDown.SUBTOTAL && renderDecklineSubtotalDisplay()}
              </Box>
            </Box>
          );
        }
        // No deckline selected
        return <Typography className='page-side-panel-text-only'>Select a Deckline</Typography>;
      }

      // Denominator Driver Content ("% Gross Margin", "% Revenue", etc.)
      if (activeTab === DriverType.DENOMINATOR_FOR_PERCENT) {
        if (selectedDeckline) {
          return (
            <Box>
              {renderDenominatorEditToolbar()}

              {denominatorType === PageSidePanelDropDown.BASE_KPI && renderDenominatorDriverDisplay()}
              {denominatorType === PageSidePanelDropDown.CALCULATED_KPI && renderDenominatorCalculatedKpiDisplay()}
            </Box>
          );
        }

        // No deckline selected
        return <Typography className='page-side-panel-text-only'>Select a Deckline</Typography>;
      }

      // Unhandled activeTab value
      return <Typography className='page-side-panel-text-only'>Unhandled Driver Type: {activeTab}</Typography>;
    }

    // Data hasn't loaded
    return <Typography className='page-side-panel-text-only'>Display Drivers Loading...</Typography>;
  }, [
    dataLoaded,
    activeTab,
    renderPageDriverDisplay,
    selectedDeckline,
    currentPageKPIDrivers,
    renderDecklineEditToolbar,
    decklineType,
    renderDecklineDriverDisplay,
    renderCalculatedKpiSegmentDisplay,
    renderDecklineAdjustmentDisplay,
    renderDecklineSubtotalDisplay,
    renderDenominatorEditToolbar,
    denominatorType,
    renderDenominatorDriverDisplay,
    renderDenominatorCalculatedKpiDisplay,
  ]);

  /**
   * Memoized TOC configuration items for the current Deck and Page View
   */
  const currTocSelectorConfig = useTocSelectorConfig(deckId, getTocItemsByGroup);

  /**
   * Determines if the current Deckline Type supports a Denominator
   */
  const isDenominatorSupported = !EXCLUDED_DECKLINES_FOR_DENOMINATORS.includes(decklineType);

  return (
    <Box>
      <TableOfContentsSelector
        deckId={deckId}
        tocPageItems={currTocSelectorConfig.currPageItems}
        viewType={ViewType.PAGE_VIEW}
        tocPageGroupLookup={currTocSelectorConfig.groupLookupValues}
      />
      <Box>
        <Tabs
          value={activeTab}
          onChange={handleDriverTabChange}
          variant='scrollable'
          scrollButtons='auto'
          aria-label='driver selection'
          className='driver-selector-tabs'
        >
          <Tab aria-label='page driver' label='Page Drivers' value={DriverType.PAGE} wrapped />
          <Tab aria-label='deckline driver' label='Deckline Drivers' value={DriverType.DECKLINE} wrapped />
          {denominatorLabel && isDenominatorSupported && (
            <Tab
              aria-label={DenominatorLabels[denominatorLabel]}
              label={DenominatorLabels[denominatorLabel]}
              value={DriverType.DENOMINATOR_FOR_PERCENT}
              wrapped
            />
          )}
        </Tabs>
      </Box>

      {/* Main Side Panel Content */}
      <Box className='page-side-panel-content'>{renderContent()}</Box>

      {/* Unsaved Subtotal Changes Dialog */}
      <ConfirmDialog
        isDialogOpen={showUnsavedDialog}
        setIsDialogOpen={setShowUnsavedDialog}
        dialogTitle='Unsaved Subtotal Changes'
        dialogText='You have unsaved subtotal changes. Do you want to save them or discard the changes?'
        confirmText='Save Subtotal'
        cancelText='Discard'
        handleConfirm={() => {
          calculateSubtotal();
          setIsCreatingSubtotal(false);
          setShowUnsavedDialog(false);
        }}
        handleCancel={discardUnsavedChanges}
      />
      <KPILibrary
        ref={kpiLibraryRef}
        rootKPI={
          selectedDeckline?.root_kpi_id
            ? (allRootKPIs.find((kpi) => kpi.root_kpi_id === selectedDeckline.root_kpi_id) ?? null)
            : null
        }
        variant={selectedEnhancedVariant}
        pageDrivers={currentPageKPIDrivers}
      />
    </Box>
  );
};

export default PageSidePanel;
